import { lazy, Suspense } from "preact/compat";
import { useEffect, useState } from "preact/hooks";
import { useLocation } from "wouter";

import { ConnectionErrorScreen } from "./components/ConnectionErrorScreen";
import { OfflineBanner } from "./components/OfflineBanner";
import { QuickAdd } from "./components/QuickAdd";
import { ReauthBanner } from "./components/ReauthBanner";
import { guidanceFor } from "./components/ReminderGuidance";
import { Sidebar } from "./components/Sidebar";
import { Snackbar } from "./components/Snackbar";
import { TabBar } from "./components/TabBar";
import { useDue, type GroupKey } from "./hooks/useDue";
import { useIsAdmin } from "./hooks/useIsAdmin";
import { useMyProjectTasks } from "./hooks/useMyProjectTasks";
import { useIsDesktop } from "./hooks/useIsDesktop";
import { useMe } from "./hooks/useMe";
import { useReminderOptIn } from "./hooks/useReminderOptIn";
import { useReminderPrefs } from "./hooks/useReminderPrefs";
import { useTeamBoard, type TeamPiece } from "./hooks/useTeamBoard";
import { useTokenInvalidFlag } from "./hooks/useTokenInvalidFlag";
import { onResetRequired } from "./lib/api";
import { timeOfDayGreeting } from "./lib/greeting";
import { confirmNavigation } from "./lib/navGuard";
import { tabKeyForView, VIEW_PATHS, viewForPath } from "./lib/routes";
import { firstName } from "./lib/personColor";
import type { DueResponse, DueTask } from "./types";
import { AnimalsScreen } from "./views/AnimalsScreen";
import { CalendarScreen } from "./views/CalendarScreen";
import { ContactsScreen } from "./views/ContactsScreen";
import { CredentialSetupView } from "./views/CredentialSetupView";
import { DashboardView } from "./views/DashboardView";
import { HandOutWorkFlow } from "./views/HandOutWorkFlow";
import { LoginView } from "./views/LoginView";
import { MoreView } from "./views/MoreView";
import { MyListView } from "./views/MyListView";
import { PasskeyEnrollView } from "./views/PasskeyEnrollView";
import { ProjectsScreen } from "./views/ProjectsScreen";
import { ReportsScreen } from "./views/ReportsScreen";
import { ResetRequiredBlockView } from "./views/ResetRequiredBlockView";
import { SettingsView } from "./views/SettingsView";
import { TeamView } from "./views/TeamView";
import { TodayView } from "./views/TodayView";
import { TokenInvalidView } from "./views/TokenInvalidView";

// Sprint-008 A1 — URL routing foundation (specs/full-web-experience-
// plan-2026-07-08.md Phase A, epic A1). `wouter` (see the PR description
// for the wouter-vs-react-router bundle-size justification) replaces the
// old `useState<TabKey>` tab held here — every tab is now a real, bookmark-
// able, deep-linkable route (`lib/routes.ts`'s `VIEW_PATHS`), with the
// EXACT same rendered output for every existing role/state combination
// (task brief item #7 — "NO visual changes").
//
// The line this sprint draws between "gets a route" and "stays local
// state": the five bottom tabs, plus `/animals` and `/projects` (both
// named explicitly in the task brief's route list, both reachable only
// from the More menu, same as before — see `lib/routes.ts`'s
// `tabKeyForView`). Everything ELSE that opens as an overlay/sub-screen
// today — Admin's People/Chores sub-tabs, the Person/Animal/Project detail
// overlays, the Chore editor, More's "Sign in & security" row, the
// "Hand out work" full-screen flow — stays exactly the local-`useState`-
// driven overlay it already was. These are all opened via an ID (or a
// small enum) held in a PARENT component's state, several levels deep in
// already-fetched list data (e.g. `PeopleView`'s `openPersonId`); making
// any of them routable would mean either lifting that fetched data up to
// a route-level loader, or wiring route params back down into an effect
// that finds-by-id in state that may not have loaded yet — real
// complexity and a real regression risk for a refactor task-briefed to
// land ALONE, with zero visual change, behind the full e2e suite. Two
// concrete, useful side effects of staying state-driven here: (1) none of
// these overlays push a history entry, so the back button is never
// "trapped" needing an extra press to escape one (task brief item #4);
// (2) the Admin People/Chores sub-tabs are EXACTLY the pattern
// `full-web-experience-plan-2026-07-08.md`'s epic A2 already plans to
// promote to first-class sidebar entries next sprint — routing them now
// would mean re-plumbing them again in two sprints for no benefit today.
//
// Sprint-008 A2 (design-desktop-phase-a.md §1) — the desktop sidebar
// shell, `≥1024px` only (`hooks/useIsDesktop.ts`). `people`/`chores`/
// `settings` join `ResolvedView` (`lib/routes.ts`) exactly as A1's own
// docstring above anticipated — People/Chores get real `/admin/people`/
// `/admin/chores` routes (`AdminView` itself picks the subtab from the
// URL — see that component), and "Your sign-in" gets `/settings`
// (`SettingsView`, new this sprint — the exact content `MoreView`'s own
// `sign-in` subview already showed, promoted to a top-level page). Below
// `1024px`, at the time, every one of these was unreachable from any
// EXISTING phone tap (`MoreView`/`AdminView`'s own local state still
// drove phone navigation) — only the sidebar linked to them.
//
// Sprint-016 F3 (MORE-01, ux-audit-whole-app-2026-07-11.md — S3) —
// "Your sign-in" is the one exception now: `MoreView`'s row calls
// `navigate("/settings")` directly (same shape every other More-landing
// row already used), so `SettingsView` is reachable from a real phone tap
// too. `AdminView`'s People/Chores sub-tabs are unaffected — still local
// state, unchanged.
//
// Sprint-008 A4 (design-desktop-phase-a.md §2) — Dashboard v1, "dashboard"
// joins `ResolvedView` too. A2 shipped the sidebar with a flagged, one-
// line-follow-up deviation ("the 'Dashboard' row is omitted... restoring
// it is a one-line change once A4 ships a real /dashboard route + view"),
// resolved now: `Sidebar.tsx`'s Dashboard row and wordmark link both point
// at `/dashboard`, and `/` itself lands there at `>=1024px` (`today` below
// it — see the `resolvedView` fallback in `AuthenticatedApp` below).
// Unlike people/chores/settings, Dashboard needs no authorization guard —
// every role reaches the ROUTE; its content is role-scoped internally
// (`views/DashboardView.tsx`'s own docstring), the same "authz lives in
// which hooks fire, not in which components render" posture `SettingsView`
// already established for `/settings`.
//
// Auth (`/login`, onboarding, reset) is untouched by this refactor per the
// task brief: `/login` is "formalized" below (the URL bar reflects it —
// see the `useEffect` in `App()`) but the actual screen the user sees is
// still 100% driven by `useMe()`'s `/me` state machine, exactly as auth v2
// (sprint-007, ADR-002 Amendment 4) built it. `?link=invalid` is
// untouched too — `useTokenInvalidFlag` reads `location.search` directly,
// entirely independent of this router (`wouter`'s default location hook
// is pathname-only), and its own early-return in `App()` still runs
// before any of this router's logic ever mounts.

// Sprint-004 B4/B5 — the Admin console (People/Chores, the QR link card,
// the chore editor) is admin-only and rarely opened even by the admin — code-
// split it into its own chunk (`preact/compat`'s `lazy`/`Suspense`, no new
// dependency) so every helper's and manager's device never downloads this
// weight at all, matching the "keep bundle lean" instruction that shaped
// the QR component's own library choice (`components/QRCode.tsx`).
const AdminView = lazy(() => import("./views/AdminView").then((m) => ({ default: m.AdminView })));

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  month: "long",
  day: "numeric",
});

export function App() {
  const me = useMe();
  const tokenInvalid = useTokenInvalidFlag();
  const [location, navigate] = useLocation();
  // Sprint-007 D6 (F4-5, UX §1.3/§0) — "either path, on success -> optional
  // Face ID/fingerprint step -> Today." This transitional state is held
  // HERE, not inside `CredentialSetupView`, so it can take priority over
  // the `needsOnboarding`/`passwordResetRequired` routing below: the
  // credential-setup screen's `onDone` sets this instead of calling
  // `me.refresh()` directly, which would otherwise flip those flags to
  // false and skip straight past the opt-in step entirely.
  const [passkeyEnrollDue, setPasskeyEnrollDue] = useState(false);
  // TD-049/TD-058 task D4 (ADR-002 Amendment 12 §1.6) — a reset-scope 403
  // observed anywhere in the app. `api.ts` broadcasts it from ONE place so
  // no hook has to remember to translate it, and it must NEVER surface as
  // "Forbidden": the credential is valid, the principal is simply out of
  // scope until they pick their own password.
  const [resetRefused, setResetRefused] = useState(false);
  // True when this page lifecycle contains a PASSWORD login — the client
  // signal that distinguishes UX's `"must-change"` (the admin typed a
  // password and read it to them) from `"reset"` (they consumed a recovery
  // magic link, which arrives as a full-page redirect with no login screen
  // in between). See the routing note below for the known limit.
  const [loggedInWithPassword, setLoggedInWithPassword] = useState(false);

  useEffect(() => onResetRequired(() => setResetRefused(true)), []);

  // Sprint-008 A1 — "formalize /login" (task brief item #2): normalizes
  // the URL bar to `/login` whenever `/me` says unauthenticated, so the
  // screen is bookmarkable/shareable — a real URL, not just whatever path
  // happened to be open when the session expired. This is the ONLY change
  // this sprint makes to the auth flow itself: which SCREEN renders is
  // still 100% `/me`-state-machine-driven (below), never gated on this
  // path — a stale/incorrect URL here can never show the wrong screen, it
  // can only fail to update the address bar. `replace` (never a pushed
  // entry) so landing here — a session expiring mid-use, or a direct nav
  // while logged out — never adds a back-button stop of its own.
  useEffect(() => {
    if (me.state.status === "unauthenticated" && location !== "/login") {
      navigate("/login", { replace: true });
    }
  }, [me.state.status, location, navigate]);

  // Sprint-007 D6 (UX §3.4, ADR-002 A4-R6) — a dead-link redirect lands
  // here regardless of session state (the redirect sets no cookie), so
  // this check runs before every other branch below.
  if (tokenInvalid) return <TokenInvalidView />;

  if (me.state.status === "loading") {
    return (
      <div class="app-shell">
        <p class="loading-hint">Loading Hayloft…</p>
      </div>
    );
  }

  if (me.state.status === "unauthenticated") {
    return (
      <LoginView
        onLoggedIn={() => {
          // Set for BOTH the typed-password and the passkey path, and that
          // is correct: Amendment 12 §2.9 makes the reset gate
          // principal-scoped, not method-scoped, so a person with a working
          // passkey who was admin-set also lands in a reset-scoped session.
          // What this flag really records is "they arrived through the login
          // screen," which is precisely what a magic-link consume (a
          // full-page redirect, no login screen) does not do.
          setLoggedInWithPassword(true);
          void me.refresh();
        }}
      />
    );
  }

  if (me.state.status === "error") {
    return (
      <ConnectionErrorScreen
        message={me.state.message}
        consecutiveFailures={me.state.consecutiveFailures}
        onRetry={me.refresh}
      />
    );
  }

  if (passkeyEnrollDue) {
    return (
      <PasskeyEnrollView
        onDone={() => {
          setPasskeyEnrollDue(false);
          void me.refresh();
        }}
      />
    );
  }

  // Sprint-007 D4 (ADR-002 Amendment 4 §4/§7, UX §8.2) — every magic-link
  // consume routes here BEFORE the main app ever renders: exactly one of
  // these two flags is true right after a consume (never both, never
  // neither), so this ordering is unambiguous. A normal password/passkey
  // login carries neither and falls through to `AuthenticatedApp` below.
  if (me.state.needsOnboarding) {
    return (
      <CredentialSetupView
        mode="first-time"
        displayName={me.state.name}
        onDone={() => setPasskeyEnrollDue(true)}
      />
    );
  }

  // TD-058 task D4 / UX §6.1 B — the SAME server flag now has two copy
  // modes. `"must-change"` is the forced change after an ADMIN-SET password
  // (the admin typed one and read it out); `"reset"` is the forced change
  // after a recovery magic-link consume. Both exit through
  // `POST /auth/password/reset`, which is one of the three routes a
  // reset-scoped principal can reach at all (Amendment 12 §1.5).
  //
  // The discriminator is the SERVER's `password_set_by_admin` (Amendment 12
  // §11.3) — true iff an admin set this password and the user has not yet
  // settled it. It replaces the "did this session arrive through the login
  // screen?" proxy this shipped with: that proxy was page-lifecycle state,
  // so a full app restart while still reset-required silently degraded an
  // admin-set password to `"reset"` copy. A server read cannot degrade.
  //
  // `returning` still uses `loggedInWithPassword`, and correctly: it asks a
  // genuinely client-side question — "did they type this password just now,
  // or are they coming back to a change they walked away from?" A restart
  // that lands here on a surviving cookie, and a refusal after abandoning
  // earlier, both read as returning, which is UX §6.1 B3's case.
  //
  // KNOWN COPY BLEMISH, recorded by the SA so it is not chased as a bug:
  // `consume_magic_link` does not clear `admin_set_expires_at`, so
  // admin-set -> plain `issue_magic_link` -> consume leaves this true and
  // shows "must-change" where "reset" is marginally apter. `issue_magic_link`
  // has no UI caller after M2, so that path is api-bearer-only. No
  // workaround here by design.
  if (me.state.passwordResetRequired) {
    const mustChange = me.state.passwordSetByAdmin;
    return (
      <CredentialSetupView
        mode={mustChange ? "must-change" : "reset"}
        returning={mustChange && !loggedInWithPassword}
        refused={resetRefused}
        displayName={me.state.name}
        onDone={() => setPasskeyEnrollDue(true)}
      />
    );
  }

  // A reset-scope 403 arrived but `/me` does not (yet) say the flag is set —
  // a request that was already in flight, a service-worker replay, or a
  // `/me` response this client is holding from before the admin acted. Never
  // render "Forbidden" for this: show UX §6.1 B2's calm full-screen block
  // and let "Pick my password" re-read `/me`, which then routes to the
  // forced-change screen above.
  if (resetRefused) {
    return <ResetRequiredBlockView onContinue={() => void me.refresh()} />;
  }

  return (
    <AuthenticatedApp
      id={me.state.id}
      name={me.state.name}
      canHandOutWork={me.state.canHandOutWork}
      isAdmin={me.state.isAdmin}
      onLogout={me.logout}
      identityOffline={me.state.offline === true}
    />
  );
}

function AuthenticatedApp({
  id,
  name,
  canHandOutWork,
  isAdmin,
  onLogout,
  identityOffline,
}: {
  id: string;
  name: string;
  canHandOutWork: boolean;
  isAdmin: boolean;
  onLogout: () => Promise<void>;
  /** True when `/me` was unreachable and the shell is running on the cached
   * identity — e.g. a captive portal where `navigator.onLine` still says
   * online, so `useDue`'s `isOffline` alone would never show the banner. */
  identityOffline: boolean;
}) {
  const {
    data,
    loading,
    error,
    isOffline,
    needsReauth,
    snackbar,
    dismissSnackbar,
    toggleTask,
    logFlexibleTask,
    refresh,
  } = useDue(id);
  const team = useTeamBoard(canHandOutWork);
  const adminGate = useIsAdmin(canHandOutWork, isAdmin);
  // Sprint-017 E7 (ADR-003 §4, design §7) — the DE-IDENTIFIED assignee feed,
  // merged inline into Today / My list below. Never flows through the /due
  // engine; carries no project identity (P0 boundary).
  const myProjectTasks = useMyProjectTasks(id);
  const [location, navigate] = useLocation();
  const [showHandOut, setShowHandOut] = useState(false);
  const isDesktop = useIsDesktop();

  // Sprint-008 A1 — the routed tab/view, replacing the old
  // `useState<TabKey>("today")`. `requestedView` is whatever the URL
  // names (`null` for "/", an unrecognized path, or anything else this
  // app doesn't know); `resolvedView` downgrades an unauthorized deep
  // link to a safe fallback SYNCHRONOUSLY, so the very first render
  // already shows the right content — no flash of the gated view, no
  // waiting on an effect (this exact contract is proven by
  // `e2e/tests/roles/member-journey.spec.ts`'s "direct nav to /team as a
  // member still renders Today, not Team" assertion, unchanged from
  // before this refactor, now enforced by this guard instead of by
  // "there was no router to honor the URL at all"). `/team` and `/admin`
  // fall back to `/today` (the app's landing default); `/projects` falls
  // back to `/more` (its own parent screen) — same shape as
  // `MoreView.tsx`'s pre-existing "Projects row is hidden entirely for a
  // member" gate, just applied to a direct URL instead of a hidden button.
  //
  // Sprint-008 A4 (design-desktop-phase-a.md §2) — "/" -> Dashboard at
  // `>=1024px`, -> Today below it (phone landing stays Today,
  // unconditionally — §1.1's own framing: "the desktop Dashboard landing
  // is A4"). `requestedView` is `null` for BOTH "/" and a genuinely
  // unrecognized path (`lib/routes.ts`'s `PATH_TO_VIEW` no longer has a
  // "/" entry at all — see that file's own docstring for why), so this
  // one `isDesktop`-gated fallback correctly covers "/" landing AND a
  // stale/typo'd bookmark identically — exactly the same "treat null the
  // same as landing on the default" contract this line always had, just
  // now viewport-aware.
  const requestedView = viewForPath(location);
  let resolvedView = requestedView ?? (isDesktop ? "dashboard" : "today");
  if (resolvedView === "team" && !canHandOutWork) resolvedView = "today";
  // Sprint-008 A2 — `people`/`chores` share the SAME admin gate as bare
  // `/admin` (they're the same admin-only console, just deep-linkable to
  // a specific subtab now — see `AdminView.tsx`). Sprint-009 B3 —
  // `activity` joins this group (a third face of the same console, per
  // ADR-002 A5 decision 5.1 — admin/api only, NOT manager). Sprint-014 C1
  // — `config` joins as a fourth face (admin/api only, ADR-002 Amendment 9
  // decision 8 — same gate, no new authorization primitive).
  if (
    (resolvedView === "admin" ||
      resolvedView === "people" ||
      resolvedView === "chores" ||
      resolvedView === "activity" ||
      resolvedView === "config") &&
    adminGate !== "admin"
  )
    resolvedView = "today";
  // Sprint-017 E7 (ADR-003 §3.3) — the Projects surface is open to EVERY
  // authenticated role now (scope is server-decided), so `/projects` is no
  // longer downgraded for a member — the former `canHandOutWork` gate here
  // is removed. (`projects-visibility` e2e still encodes the OLD flat-role
  // model and is owned by FE-3.)
  // Sprint-009 B4 (design-phase-b.md §1.1) — Reports is `canHandOutWork`-
  // gated (admin OR manager, per ADR-002 A5 decision 6), same guard/
  // fallback shape as Projects above.
  if (resolvedView === "reports" && !canHandOutWork) resolvedView = "more";
  // Sprint-010 C4 (design-phase-c.md §1.1) — Contacts is `canHandOutWork`-
  // gated (admin OR manager, ADR-002 A6 decision 8.2), same guard/fallback
  // shape as Projects/Reports above.
  if (resolvedView === "contacts" && !canHandOutWork) resolvedView = "more";
  const tab = tabKeyForView(resolvedView);

  // Normalizes the URL bar to the resolved view's canonical path — covers
  // "/" -> "/today" (item #6), an unrecognized path -> "/today", and every
  // downgrade above. `replace` so none of these add their own back-button
  // stop (only a real tab switch, via `TabBar`'s `onChange` below, should
  // do that — task brief item #4).
  useEffect(() => {
    const canonical = VIEW_PATHS[resolvedView];
    if (location !== canonical) navigate(canonical, { replace: true });
  }, [location, resolvedView, navigate]);

  // Sprint-005 C6 — one shared `useReminderPrefs()` instance for the whole
  // authenticated session: both the Today opt-in card ("Yes please") and
  // the More tab's "Daily reminders" toggle read/act through it, so there
  // is exactly one `PushManager.getSubscription()` probe and one
  // subscribe/unsubscribe code path, never two independently-drifting
  // copies of "is this device subscribed."
  const reminderPrefs = useReminderPrefs();
  const reminderOptIn = useReminderOptIn(reminderPrefs.support);

  const dateLabel = data ? DATE_FORMATTER.format(new Date(`${data.date}T00:00:00`)) : "";

  async function refreshEverything() {
    await Promise.all([refresh(), team.refresh()]);
  }

  // Sprint-014 C1 (config-page-ia-layout-2026-07-10.md §9) — every
  // Sidebar-row / wordmark / phone-TabBar navigation routes through this
  // instead of calling `navigate` directly, so the Config page's "discard
  // unsaved changes?" `ConfirmDialog` gets a chance to block the nav (see
  // `lib/navGuard.ts` for the registered-guard mechanism and its
  // documented scope limits).
  async function guardedNavigate(href: string) {
    if (await confirmNavigation()) navigate(href);
  }

  function handleTogglePiece(piece: TeamPiece) {
    void (piece.done ? team.uncompletePiece(piece.task.id) : team.completePiece(piece.task.id));
  }

  // Sprint-017 E7 — a de-identified project task (id in the `/me/project-
  // tasks` feed) toggles via the de-identified PATCH path, NEVER the /due
  // completion path (it has no cadence/assignment row). Returns true when it
  // handled the toggle. `group !== "done_today"` means "was open → mark
  // done"; from done_today it un-checks.
  function routeProjectTaskToggle(task: DueTask, group: GroupKey): boolean {
    if (!myProjectTasks.ids.has(task.id)) return false;
    void myProjectTasks.toggle(task.id, group !== "done_today");
    return true;
  }

  // Records "first meaningful use" for the opt-in heuristic on every
  // MARK-DONE tap (never on undo) — mirrors `useDue.ts::toggleTask`'s own
  // `group === "done_today"` check for "this is an undo, not a completion."
  function handleToggle(task: DueTask, group: GroupKey) {
    if (routeProjectTaskToggle(task, group)) return;
    if (group !== "done_today") reminderOptIn.recordCompletion();
    toggleTask(task, group);
  }

  // My list's toggle (no reminder-opt-in heuristic — that's a Today-only
  // nudge) with the same de-identified interception.
  function handleMyListToggle(task: DueTask, group: GroupKey) {
    if (routeProjectTaskToggle(task, group)) return;
    toggleTask(task, group);
  }

  // The de-identified feed merged INLINE into the caller's own list (ADR-003
  // §4.5 / ruling #9): undated project tasks join "Whenever you get a
  // chance"; completed ones join "Done today". A presentation-layer union
  // only — never a separate "assigned to me" section, never through the
  // /due engine.
  const mergedData: DueResponse | null = data
    ? {
        ...data,
        whenever: [...data.whenever, ...myProjectTasks.openTasks],
        done_today: [...data.done_today, ...myProjectTasks.doneTasks],
      }
    : data;

  // Newness dot on the My list tab, cleared when the person opens My list.
  useEffect(() => {
    if (resolvedView === "mylist") myProjectTasks.markSeen();
  }, [resolvedView, myProjectTasks]);

  function handleAcceptReminders() {
    reminderOptIn.accept();
    void reminderPrefs.enable();
  }

  // ADR-008 — cadence-backed Due-today rows are included alongside
  // assignment-kind rows so "Hand out work" step 1's pick list isn't
  // stripped empty on farms where most due-today chores are cadence-backed.
  // `whenever` is assignment-kind only per schema; `overdue` stays excluded
  // (out of scope — Due-today only).
  const myOpenAssignmentTasks = data ? [...data.due_today, ...data.whenever] : [];

  // Sprint-010 C4 (design-phase-c.md §5.4) — the same roster shape
  // `CalendarScreen`'s "Who does it" picker already derives from the
  // already-fetched `useTeamBoard` data (no second fetch); reused
  // verbatim for Quick Add's "Add a task" flow.
  const roster = (team.board?.members ?? []).map((m) => ({ id: m.user_id, name: m.name }));

  return (
    <div class="app-shell">
      {/* Sprint-008 A2 — `≥1024px` only; never mounted alongside `TabBar`
       * below (design-desktop-phase-a.md §1.1: "the sidebar and TabBar
       * are mutually exclusive — never both mounted"). */}
      {isDesktop ? (
        <Sidebar
          active={resolvedView}
          canHandOutWork={canHandOutWork}
          isAdmin={adminGate === "admin"}
          id={id}
          name={name}
          onNavigate={(href) => void guardedNavigate(href)}
          onLogout={() => void onLogout()}
        />
      ) : null}

      <div class="main-shell">
        {/* Sprint-008 A2/A4 — JS-gated (not just CSS-hidden), matching
         * Sidebar/TabBar: `.sidebar-footer` (below) is the desktop
         * replacement for this row's identity (+ "Sign out", which on phone
         * lives on More / Your sign-in instead) — see
         * §1.5. `dateLabel`'s desktop-visible home (the app-bar's top row,
         * on phone) is now `DashboardView`'s own `.view-subheading` (§2.2)
         * — passed down as a prop below rather than recomputed there, so
         * there is exactly one `DATE_FORMATTER.format()` call site. Every
         * OTHER desktop route (Today/My list/Team/Admin/Animals/Projects/
         * Settings) still has no on-screen date — only Dashboard ever
         * showed one, matching the phone app-bar's own single-owner
         * behavior pre-A4. */}
        {isDesktop ? null : (
          <header class="app-bar">
            <div class="app-bar-top">
              <span class="app-wordmark">Hayloft</span>
              <span class="app-date">{dateLabel}</span>
            </div>
            <div class="app-bar-greeting-row">
              <div class="greeting">
                {timeOfDayGreeting()}, {firstName(name) || name}
              </div>
              {/* UX review 2026-10 — no sign-out here any more. The old
               * instant "Done for now" button sat next to the greeting on
               * every screen and read as "I'm finished with my chores";
               * sign-out now lives on More and Your sign-in, behind a
               * confirm (`components/SignOutButton.tsx`). */}
            </div>
          </header>
        )}

        <OfflineBanner visible={(isOffline || identityOffline) && !needsReauth} />
        <ReauthBanner visible={needsReauth} />

        <main class="view-area">
          {loading ? (
            <p class="loading-hint">Loading today's list…</p>
          ) : error ? (
            <p class="error-hint" role="alert">
              {error}
            </p>
          ) : data ? (
            resolvedView === "dashboard" ? (
              <DashboardView
                name={name}
                dateLabel={dateLabel}
                canHandOutWork={canHandOutWork}
                data={mergedData ?? data}
                team={team}
                onNavigate={navigate}
              />
            ) : resolvedView === "today" ? (
              <TodayView
                data={mergedData ?? data}
                onToggle={handleToggle}
                canHandOutWork={canHandOutWork}
                onHandOutWork={() => setShowHandOut(true)}
                onOpenParent={() => navigate("/team")}
                onLogFlexible={logFlexibleTask}
                showReminderPrompt={reminderOptIn.showPrompt}
                onAcceptReminders={handleAcceptReminders}
                onDeclineReminders={reminderOptIn.decline}
                reminderGuidance={
                  reminderPrefs.support === "denied"
                    ? null
                    : guidanceFor(reminderPrefs.support, reminderPrefs.device)
                }
              />
            ) : resolvedView === "mylist" ? (
              <MyListView
                data={mergedData ?? data}
                onToggle={handleMyListToggle}
                onLogFlexible={logFlexibleTask}
              />
            ) : resolvedView === "team" && canHandOutWork ? (
              team.loading ? (
                <p class="loading-hint">Loading the team…</p>
              ) : team.error ? (
                <p class="error-hint" role="alert">
                  {team.error}
                </p>
              ) : team.board ? (
                <TeamView
                  board={team.board}
                  teamDue={team.teamDue}
                  meId={id}
                  onTogglePiece={handleTogglePiece}
                  onMarkParentDone={(parentId) => void team.completePiece(parentId)}
                  onUndoParentDone={(parentId) => void team.uncompletePiece(parentId)}
                />
              ) : null
            ) : (resolvedView === "admin" ||
                resolvedView === "people" ||
                resolvedView === "chores" ||
                resolvedView === "activity" ||
                resolvedView === "config") &&
              adminGate === "admin" ? (
              <Suspense fallback={<p class="loading-hint">Loading Admin…</p>}>
                {/* `key={resolvedView}` forces a remount on every People
                 * <-> Chores <-> Activity <-> Config <-> bare-Admin
                 * transition (all five render this SAME component) — the
                 * one thing that lets `AdminView`'s own
                 * `useFocusHeadingOnMount` correctly refocus its heading on
                 * a genuine desktop sidebar-row activation (R-A0-2),
                 * reusing the existing mount-only hook rather than
                 * inventing a second focus-management convention for this
                 * one component. */}
                {/* TD-058 / Amendment 12 §2.4 — `currentUserId` exists for
                 * exactly one reason: the recovery action must NOT be
                 * offered on the admin's own person card. A self-target
                 * 409s server-side, but discovering that by tapping it is
                 * the wrong shape — `revoke_all_user_credentials` has no
                 * `keep_session_id`, so the guard is what stands between her
                 * and killing her own session while replacing her own
                 * password with a value she typed once, unverified. */}
                <AdminView key={resolvedView} currentUserId={id} />
              </Suspense>
            ) : resolvedView === "more" ? (
              <MoreView
                canHandOutWork={canHandOutWork}
                isAdmin={adminGate === "admin"}
                reminderPrefs={reminderPrefs}
                onSignOut={onLogout}
              />
            ) : resolvedView === "reports" && canHandOutWork ? (
              <ReportsScreen canHandOutWork={canHandOutWork} onBack={() => navigate("/more")} />
            ) : resolvedView === "calendar" ? (
              <CalendarScreen
                data={data}
                canHandOutWork={canHandOutWork}
                roster={roster}
                onNavigate={navigate}
                onBack={() => navigate("/more")}
              />
            ) : resolvedView === "animals" ? (
              <AnimalsScreen isAdmin={adminGate === "admin"} onBack={() => navigate("/more")} />
            ) : resolvedView === "projects" ? (
              <ProjectsScreen
                canHandOutWork={canHandOutWork}
                isAdmin={adminGate === "admin"}
                meId={id}
                roster={roster}
                onBack={() => navigate("/more")}
              />
            ) : resolvedView === "contacts" && canHandOutWork ? (
              <ContactsScreen
                canHandOutWork={canHandOutWork}
                isAdmin={adminGate === "admin"}
                onBack={() => navigate("/more")}
              />
            ) : resolvedView === "settings" ? (
              <SettingsView
                reminderPrefs={reminderPrefs}
                onBack={() => navigate("/more")}
                onSignOut={onLogout}
              />
            ) : null
          ) : null}
        </main>

        {isDesktop ? null : (
          <TabBar
            active={tab}
            onChange={(key) => void guardedNavigate(VIEW_PATHS[key])}
            showTeam={canHandOutWork}
            showAdmin={adminGate === "admin"}
            mylistUnread={myProjectTasks.unreadCount > 0}
          />
        )}

        <Snackbar state={snackbar} onDismiss={dismissSnackbar} />

        {showHandOut ? (
          <HandOutWorkFlow
            roster={team.board?.members ?? []}
            myOpenTasks={myOpenAssignmentTasks}
            onClose={() => setShowHandOut(false)}
            onSent={refreshEverything}
          />
        ) : null}
      </div>

      {/* Sprint-010 C4 (design-phase-c.md §5.1/§5.2) — mounted once, as a
       * fixed-position sibling of `.main-shell`, OUTSIDE the routed
       * `<main class="view-area">` subtree entirely — no view's own
       * loading/error/tab state can ever unmount it (§8's TD-010 rule,
       * applied to the button/FAB itself). Absent (not present-and-inert)
       * for `canHandOutWork === false` — every one of its four actions
       * needs at least that gate, per §0's own "no dead affordances"
       * ruling (validated, no change — §11.2). */}
      <QuickAdd canHandOutWork={canHandOutWork} isAdmin={adminGate === "admin"} roster={roster} />
    </div>
  );
}
